import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    TextField,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    TablePagination,
    Tabs,
    Tab,
    Button,
    CircularProgress,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { API_URL } from '../../api';
import AccountStatusChips from '../../components/AccountStatusChips';
import HowItWorksCard from '../../components/HowItWorksCard';
import { AccountLifecycleHelp } from '../../components/AccountLifecycleHelp';

const cookies = new Cookies();

const LEVELS = ['', 'KS2', 'KS3', 'GCSE', 'A-Level'];

// Options for the Active-tab account-status dropdown. The Archived value
// lives on its own tab now, so we drop it from this list to avoid two
// competing ways of surfacing the same rows.
const ACCOUNT_STATUS_OPTIONS = [
    { value: '', label: 'Any account status' },
    { value: 'pending_approval', label: 'Pending approval' },
    { value: 'approved', label: 'Approved' },
    { value: 'declined', label: 'Declined' },
];
const ENGAGEMENT_STATUS_OPTIONS = [
    { value: '', label: 'Any engagement' },
    { value: 'enrolled', label: 'Enrolled' },
    { value: 'not_enrolled', label: 'Not enrolled' },
    { value: 'on_break', label: 'On break' },
];

export default function ParentsPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [level, setLevel] = useState('');
    const [subjects, setSubjects] = useState([]);
    const [subject, setSubject] = useState('');
    const [accountStatus, setAccountStatus] = useState('');
    const [engagementStatus, setEngagementStatus] = useState('');
    // 'active' hides archived by default (via excludeArchived=1 on the API).
    // 'archived' shows *only* archived. Any other status filter is disabled
    // in the Archived view since it's redundant.
    const [tab, setTab] = useState('active');

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('role', 'parent');
            params.set('page', page + 1);
            params.set('limit', rowsPerPage);
            if (q.trim()) params.set('q', q.trim());
            if (level) params.set('level', level);
            if (subject) params.set('subject', subject);
            if (tab === 'archived') {
                params.set('accountStatus', 'archived');
            } else {
                if (accountStatus) params.set('accountStatus', accountStatus);
                else params.set('excludeArchived', '1');
            }
            if (engagementStatus) params.set('engagementStatus', engagementStatus);
            const { data } = await axios.get(`${API_URL}/api/admin/accounts?${params.toString()}`, {
                headers: { Authorization: cookies.get('TOKEN') },
            });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        axios
            .get(`${API_URL}/api/subjects`)
            .then((res) => setSubjects(res.data || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        load();
    }, [page, rowsPerPage, tab]);

    const submitFilters = (e) => {
        e.preventDefault();
        if (page !== 0) setPage(0);
        else load();
    };

    return (
        <Container>
            <Typography variant="h4">Parents</Typography>
            <HowItWorksCard>
                <AccountLifecycleHelp roleLabel="parent" />
            </HowItWorksCard>
            <Box sx={{ mb: 3 }} />

            <Tabs
                value={tab}
                onChange={(_, v) => {
                    setTab(v);
                    if (page !== 0) setPage(0);
                }}
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab value="active" label="Active" />
                <Tab value="archived" label="Archived" />
            </Tabs>

            <Card sx={{ p: 2, mb: 3 }}>
                <Box component="form" onSubmit={submitFilters} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        label="Search name / email / student"
                        size="small"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        sx={{ minWidth: 260 }}
                    />
                    <TextField
                        select
                        size="small"
                        label="Level"
                        value={level}
                        onChange={(e) => setLevel(e.target.value)}
                        sx={{ minWidth: 140 }}
                    >
                        {LEVELS.map((l) => (
                            <MenuItem key={l || 'any'} value={l}>
                                {l || 'Any'}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        size="small"
                        label="Subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        sx={{ minWidth: 200 }}
                    >
                        <MenuItem value="">Any</MenuItem>
                        {subjects.map((s) => (
                            <MenuItem key={s._id} value={s.name}>
                                {s.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    {tab === 'active' && (
                        <TextField
                            select
                            size="small"
                            label="Account status"
                            value={accountStatus}
                            onChange={(e) => setAccountStatus(e.target.value)}
                            sx={{ minWidth: 180 }}
                        >
                            {ACCOUNT_STATUS_OPTIONS.map((o) => (
                                <MenuItem key={o.value || 'any'} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    <TextField
                        select
                        size="small"
                        label="Engagement"
                        value={engagementStatus}
                        onChange={(e) => setEngagementStatus(e.target.value)}
                        sx={{ minWidth: 170 }}
                    >
                        {ENGAGEMENT_STATUS_OPTIONS.map((o) => (
                            <MenuItem key={o.value || 'any'} value={o.value}>
                                {o.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Button type="submit" variant="contained">
                        Filter
                    </Button>
                </Box>
            </Card>

            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}>
                        <CircularProgress />
                    </Stack>
                ) : (
                    <>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Parent</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Children</TableCell>
                                    <TableCell>Tutor</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((p) => (
                                    <TableRow key={p._id}>
                                        <TableCell>
                                            {p.firstName} {p.surname}
                                        </TableCell>
                                        <TableCell>{p.email}</TableCell>
                                        <TableCell sx={{ minWidth: 220 }}>
                                            {(() => {
                                                const linked = Array.isArray(p.linkedChildren) ? p.linkedChildren : [];
                                                const legacy = p.childInfo?.studentName;
                                                if (linked.length === 0 && !legacy) return '—';
                                                if (linked.length === 0 && legacy) {
                                                    return (
                                                        <Typography variant="body2">
                                                            {legacy}
                                                            {p.childInfo?.yearGroup ? ` · ${p.childInfo.yearGroup}` : ''}
                                                            {p.childInfo?.subject ? ` · ${p.childInfo.subject}` : ''}
                                                        </Typography>
                                                    );
                                                }
                                                return (
                                                    <Stack spacing={0.25}>
                                                        {linked.map((c) => (
                                                            <Typography key={c._id} variant="body2">
                                                                {c.firstName} {c.surname}
                                                                {c.childInfo?.yearGroup ? ` · ${c.childInfo.yearGroup}` : ''}
                                                                {c.childInfo?.subject ? ` · ${c.childInfo.subject}` : ''}
                                                            </Typography>
                                                        ))}
                                                    </Stack>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            {p.assignedTutorId
                                                ? `${p.assignedTutorId.firstName} ${p.assignedTutorId.surname}`
                                                : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <AccountStatusChips account={p} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button component={RouterLink} to={`/parents/${p._id}`} size="small">
                                                Open
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {items.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                                No parents found.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </>
                )}
            </Card>
        </Container>
    );
}
